import { useCallback, useEffect, useState } from 'react';
import { withTheme, Portal } from '@material-ui/core';
import ChatWindow from './chat/ChatWindow';
import { CommentIcon } from '../icons';
import { useSelector } from 'react-redux';

const UNREAD_KEY = 'chat_unread_v1';

const safeParseUnread = raw => {
    if (!raw) {
        return {};
    }
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        const out = {};
        for (const key of Object.keys(parsed)) {
            const n = Number(parsed[key]);
            if (Number.isFinite(n) && n > 0) {
                out[key] = n;
            }
        }
        return out;
    } catch {
        return {};
    }
};

// The subdomain shell sets window.ACTIVITER_UI_SWITCH to the other
// frontend ('new' | 'legacy'); embedders never set it. The link only
// shows on the main page, and shifts left when the agent chat bubble is
// present so it doesn't sit under it.
const uiSwitchTarget = window.ACTIVITER_UI_SWITCH;
const showUiSwitch =
    window.location.pathname === '/' && (uiSwitchTarget === 'new' || uiSwitchTarget === 'legacy');

const uiSwitchHref = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('ui', uiSwitchTarget);
    return url.toString();
};

const Footer = ({ theme }) => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    let emails = window.clientData?.contact_email?.customer?.replace(/\s/g, '')?.split(',') || [];
    const user = useSelector(state => state.user?.user);
    const isAgent = user?.role === 'agent';

    const [unreadByUser, setUnreadByUserState] = useState(() =>
        safeParseUnread(localStorage.getItem(UNREAD_KEY))
    );

    const setUnreadByUser = useCallback(updater => {
        setUnreadByUserState(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            try {
                localStorage.setItem(UNREAD_KEY, JSON.stringify(next));
            } catch {}
            return next;
        });
    }, []);

    useEffect(() => {
        const onStorage = e => {
            if (e.key !== UNREAD_KEY) {
                return;
            }
            setUnreadByUserState(safeParseUnread(e.newValue));
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        if (!user) {
            setIsChatOpen(false);
        }
    }, [user]);

    const totalUnread = Object.values(unreadByUser).reduce(
        (sum, n) => sum + (Number.isFinite(n) ? n : 0),
        0
    );
    const unreadLabel = totalUnread > 9 ? '9+' : String(totalUnread);

    return (
        <>
            <div
                className="footer"
                style={{
                    backgroundColor: theme.palette.primary.main,
                    position: 'relative',
                    textAlign: 'center'
                }}
            >
                Questions? Contact us at
                <br />
                <a href={`tel:${window.clientData?.contact_phone?.customer}`}>
                    {window.clientData?.contact_phone?.customer}
                </a>{' '}
                <br />
                {emails.map(email => {
                    return <a key={email} href={`mailto:${email}`}>{`${email} `}</a>;
                })}
                {showUiSwitch && (
                    <a
                        href={uiSwitchHref()}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            right: isAgent ? 96 : 16,
                            transform: 'translateY(-50%)',
                            background: 'rgb(17,17,17)',
                            color: 'white',
                            padding: '10px 14px',
                            borderRadius: 999,
                            font:
                                '600 13px/1 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
                            textDecoration: 'none',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {uiSwitchTarget === 'new'
                            ? 'Try the new experience'
                            : 'Switch to classic view'}
                    </a>
                )}
            </div>

            {isAgent && (
                <Portal>
                    <div
                        className={`chatBubble ${isChatOpen ? 'active' : ''}`}
                        onClick={() => setIsChatOpen(prev => !prev)}
                    >
                        <CommentIcon style={{ fontSize: 24 }} />
                        {totalUnread > 0 && (
                            <span className="chatBubbleNodge" aria-label={`${totalUnread} unread`}>
                                {unreadLabel}
                            </span>
                        )}
                    </div>
                    <div className={`chatDock ${isChatOpen ? 'open' : ''}`}>
                        <ChatWindow
                            isChatOpen={isChatOpen}
                            setIsChatOpen={setIsChatOpen}
                            unreadByUser={unreadByUser}
                            setUnreadByUser={setUnreadByUser}
                        />
                    </div>
                </Portal>
            )}
        </>
    );
};

export default withTheme(Footer);
